import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationTutorial } from './steps/NavigationTutorial';
import { PersonaSelection } from './steps/PersonaSelection';
import { HandleClaim } from './steps/HandleClaim';
import { Background } from '../portal/components/aeroglass/Background';
import { PWAInstallPrompt } from '../../components/PWAInstallPrompt';

export type OnboardingStep = 'tutorial' | 'persona' | 'claim' | 'install';
export type UserPersona = 'fisherman' | 'business' | 'influencer';

export const OnboardingWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('tutorial');
  const [selectedPersona, setSelectedPersona] = useState<UserPersona | null>(null);
  const navigate = useNavigate();

  // Ensure we are in a clean state
  useEffect(() => {
    // Optional: Check if already completed?
  }, []);

  const handleTutorialComplete = () => {
    setCurrentStep('persona');
  };

  const handlePersonaSelect = (persona: UserPersona) => {
    setSelectedPersona(persona);
    setCurrentStep('claim');
  };

  const handleClaimComplete = () => {
    // Proceed to install step instead of finishing
    setCurrentStep('install');
  };

  const finalizeOnboarding = () => {
    // Finalize onboarding
    localStorage.setItem('hasSeenOnboarding', 'true');
    
    if (selectedPersona === 'business') {
      navigate('/cadastrar-empresa');
    } else {
      navigate('/admin/profile'); // Redirect to profile
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0F172A] text-white font-sans flex flex-col items-center justify-center">
      {/* Reusing Aeroglass Background for consistency */}
      <Background isMenuOpen={false} />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-md p-6 flex flex-col items-center">
        
        {/* Step: Navigation Tutorial */}
        {currentStep === 'tutorial' && (
          <NavigationTutorial onComplete={handleTutorialComplete} />
        )}

        {/* Step: Persona Selection */}
        {currentStep === 'persona' && (
          <PersonaSelection onSelect={handlePersonaSelect} />
        )}

        {/* Step: Handle Claim */}
        {currentStep === 'claim' && selectedPersona && (
          <HandleClaim 
            persona={selectedPersona} 
            onComplete={handleClaimComplete}
            onBack={() => setCurrentStep('persona')}
          />
        )}

        {/* Step: PWA Install */}
        {currentStep === 'install' && (
           <PWAInstallPrompt 
              onInstall={finalizeOnboarding}
              onDismiss={finalizeOnboarding}
           />
        )}

      </div>
      
      {/* Progress Dots (Optional, hidden on tutorial) */}
      {currentStep !== 'tutorial' && (
        <div className="absolute bottom-10 left-0 w-full flex justify-center gap-2 z-20">
          <div className={`w-2 h-2 rounded-full transition-colors ${currentStep === 'persona' ? 'bg-white' : 'bg-white/30'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${currentStep === 'claim' ? 'bg-white' : 'bg-white/30'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${currentStep === 'install' ? 'bg-white' : 'bg-white/30'}`} />
        </div>
      )}
    </div>
  );
};
